'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useRouter, useSearchParams } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useI18n } from '@/hooks/useI18n';
import { useAuthStore } from '@/lib/store';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(2, 'Password must be at least 6 characters'),
  rememberMe: z.boolean()
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
  const { t } = useI18n();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const { login } = useAuthStore();
  const oauthTxId = searchParams?.get('oauth_tx') || '';

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });

  const onSubmit = async (data: LoginFormData) => {
    console.log('[LOGIN PAGE] onSubmit called with email:', data.email);
    setIsLoading(true);
    setErrorMessage(''); // Clear previous error messages
    setSuccessMessage(''); // Clear previous success messages

    try {
      console.log('[LOGIN PAGE] Calling store login function...');
      // Call the login function from Zustand store with user input
      await login(data.email, data.password);

      console.log('[LOGIN PAGE] Login successful! Setting success message and scheduling redirect');
      setSuccessMessage(t('auth.loginSuccess'));

      // Redirect to the MCP callback flow when this login started from OAuth.
      const nextDestination = oauthTxId
        ? `/auth/callback?oauth_tx=${encodeURIComponent(oauthTxId)}`
        : '/';
      console.log('[LOGIN PAGE] Scheduling redirect to', nextDestination, 'in 1000ms');
      setTimeout(() => {
        console.log('[LOGIN PAGE] Executing redirect to', nextDestination);
        console.log('[LOGIN PAGE] Current pathname before redirect:', window.location.pathname);
        router.push(nextDestination);
        // Also try window.location as fallback
        setTimeout(() => {
          console.log('[LOGIN PAGE] Checking if redirect worked, current pathname:', window.location.pathname);
          if (window.location.pathname === '/login') {
            console.warn('[LOGIN PAGE] Still on login page, trying window.location.href');
            window.location.href = nextDestination;
          }
        }, 500);
      }, 1000);
    } catch (error: any) {
      console.error('[LOGIN PAGE] Login failed:', error);

      // Set appropriate error message based on the error
      if (error.message) {
        // Check if it's one of our predefined error messages
        if (error.message.includes('Invalid email or password')) {
          setErrorMessage(t('auth.invalidCredentials'));
        } else if (error.message.includes('Network error')) {
          setErrorMessage(t('auth.networkError'));
        } else {
          setErrorMessage(error.message);
        }
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setErrorMessage(t('auth.networkError'));
      } else {
        setErrorMessage(t('auth.loginFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 lg:max-w-md xl:max-w-lg flex flex-col justify-center px-4 sm:px-6 lg:px-8 bg-[hsl(var(--color-background))] relative">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6" data-snapland-id="login-page-language-switcher">
          <LanguageSwitcher variant="compact" />
        </div>
        <div className="mx-auto w-full max-w-sm">
          {/* Logo Section */}
          <div className="mb-8" data-snapland-id="login-page-header">
            <div className="flex items-center mb-6" data-snapland-id="login-page-logo-container">
              <img
                src="/snapland-logo.svg"
                alt="Snapland Logo"
                className="h-8"
                data-snapland-id="login-page-logo"
              />
            </div>
            <h2 className="text-3xl font-bold text-[hsl(var(--color-foreground))] mb-2" data-snapland-id="login-page-title">{t('auth.welcomeBack')}</h2>
            <p className="text-[hsl(var(--color-muted-foreground))]" data-snapland-id="login-page-subtitle">{t('auth.welcomeBackSubtitle')}</p>
          </div>

          {/* Login Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" data-snapland-id="login-form">
              {/* Email Field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem data-snapland-id="login-form-email-field">
                    <FormLabel className="text-[hsl(var(--color-foreground))]" snaplandId="login-form-email-label">{t('auth.email')}</FormLabel>
                    <FormControl>
                      <div className="relative" data-snapland-id="login-form-email-input-container">
                        <Mail className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--color-muted-foreground))] w-4 h-4" data-snapland-id="login-form-email-icon" />
                        <Input {...field} type="email" className="pl-10 rtl:pl-3 rtl:pr-10" placeholder={t('form.enterEmail')} snaplandId="login-form-email-input" />
                      </div>
                    </FormControl>
                    <FormMessage data-snapland-id="login-form-email-error" />
                  </FormItem>
                )}
              />

              {/* Password Field */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem data-snapland-id="login-form-password-field">
                    <FormLabel className="text-[hsl(var(--color-foreground))]" snaplandId="login-form-password-label">{t('auth.password')}</FormLabel>
                    <FormControl>
                      <div className="relative" data-snapland-id="login-form-password-input-container">
                        <Lock className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--color-muted-foreground))] w-4 h-4" data-snapland-id="login-form-password-icon" />
                        <Input {...field} type={showPassword ? 'text' : 'password'} className="pl-10 pr-10 rtl:pl-10 rtl:pr-10" placeholder={t('form.enterPassword')} autoComplete='current-password' snaplandId="login-form-password-input" />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 rtl:right-auto rtl:left-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--color-muted-foreground))] hover:text-[hsl(var(--color-foreground))] transition-colors"
                          data-snapland-id="login-form-password-toggle-button"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" data-snapland-id="login-form-password-toggle-icon" /> : <Eye className="w-4 h-4" data-snapland-id="login-form-password-toggle-icon" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage data-snapland-id="login-form-password-error" />
                  </FormItem>
                )}
              />

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                {/* <FormField 
                  control={form.control} 
                  name="rememberMe" 
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm text-[hsl(var(--color-foreground))]">
                          {t('auth.rememberMe')}
                        </FormLabel>
                      </div>
                    </FormItem>
                  )} 
                /> */}
                {/* <button 
                  type="button"
                  onClick={() => router.push('/auth/forgot-password')}
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  {t('auth.forgotPassword')}
                </button> */}
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="p-3 rounded-md bg-[hsl(var(--color-destructive))]/10 border border-[hsl(var(--color-destructive))]/20" data-snapland-id="login-form-error-message">
                  <p className="text-sm text-[hsl(var(--color-destructive))]" data-snapland-id="login-form-error-text">
                    {errorMessage}
                  </p>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="p-3 rounded-md bg-[hsl(var(--color-success))]/10 border border-[hsl(var(--color-success))]/20" data-snapland-id="login-form-success-message">
                  <p className="text-sm text-[hsl(var(--color-success))]" data-snapland-id="login-form-success-text">
                    {successMessage}
                  </p>
                </div>
              )}

              {/* Login Button */}
              <Button type="submit" className="w-full bg-[hsl(var(--sidebar-background))] hover:bg-[hsl(var(--sidebar-background))] text-[hsl(var(--color-primary-foreground))]" disabled={isLoading} snaplandId="login-form-submit-button">
                {isLoading ? t('common.loading') : t('auth.logIn')}
              </Button>
              {/* <button 
                  type="button"
                  onClick={() => router.push('/auth/forgot-password')}
                  className="text-sm text-[hsl(var(--color-primary))] hover:text-[hsl(var(--color-primary))/0.8] transition-colors"
                >
                  {t('auth.forgotPassword')}
            </button> */}
            </form>
          </Form>

          {/* Copyright */}
          <div className="mt-8 text-center" data-snapland-id="login-page-footer">
            <p className="text-xs text-[hsl(var(--color-muted-foreground))]" data-snapland-id="login-page-copyright">
              © {t('common.year')} {t('common.snapland')}. {t('auth.allRightsReserved')}
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Dashboard Preview */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-[hsl(var(--sidebar-background))]" data-snapland-id="login-page-preview-section">
        <div className="flex items-center justify-center w-full h-full p-8" data-snapland-id="login-page-preview-container">
          <div className="max-w-4xl w-full flex items-center justify-center" data-snapland-id="login-page-preview-content">
            <div className="text-center" data-snapland-id="login-page-preview-text">
              <h3 className="text-2xl font-bold text-[hsl(var(--color-primary-foreground))] mb-4" data-snapland-id="login-page-preview-title">
                {t('auth.welcomeTo')} {t('common.snapland')}
              </h3>
              <p className="text-[hsl(var(--color-primary-foreground))] opacity-80" data-snapland-id="login-page-preview-description">
                {t('auth.platformDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
